import React, { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import API from "../../../../api";
import "./TasksPage.css";

export default function TasksPage({ projectId }) {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    project_id: projectId ? String(projectId) : "",
    milestone_id: "",
    due_date: "",
    status: "todo",
    priority: "medium",
    estimated_hours: "",
  });

  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [detailTask, setDetailTask] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    loadProjects();
    loadEmployees();
    if (projectId) {
      setForm((f) => ({ ...f, project_id: String(projectId) }));
      loadTasks(projectId);
      loadMilestones(projectId);
    } else {
      setTasks([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function loadProjects() {
    try {
      const res = await API.get("/projects");
      setProjects(res.data);
    } catch (err) {
      console.error("Projects error", err.response?.data || err.message);
    }
  }

  async function loadEmployees() {
    try {
      const res = await API.get("/employees");
      setEmployees(res.data);
    } catch (err) {
      console.error("Employees error", err.response?.data || err.message);
    }
  }

  async function loadMilestones(pid) {
    if (!pid) {
      setMilestones([]);
      return;
    }
    try {
      const res = await API.get(`/milestones/project/${pid}`);
      setMilestones(res.data);
    } catch (err) {
      console.error("Milestones error", err.response?.data || err.message);
    }
  }

  async function loadTasks(pid) {
    if (!pid) {
      setTasks([]);
      return;
    }
    try {
      const res = await API.get(`/tasks/project/${pid}`);
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Tasks error", err.response?.data || err.message);
      setTasks([]);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function openModal(task = null) {
    if (task) {
      setSelectedTask(task);
      setForm({
        title: task.title || "",
        description: task.description || "",
        project_id: task.project_id ? String(task.project_id) : "",
        milestone_id: task.milestone_id ? String(task.milestone_id) : "",
        due_date: task.due_date ? task.due_date.split("T")[0] : "",
        status: task.status || "todo",
        priority: task.priority || "medium",
        estimated_hours:
          task.estimated_hours != null ? String(task.estimated_hours) : "",
      });
      if (task.project_id) loadMilestones(task.project_id);
    } else {
      setSelectedTask(null);
      setForm({
        title: "",
        description: "",
        project_id: projectId ? String(projectId) : "",
        milestone_id: "",
        due_date: "",
        status: "todo",
        priority: "medium",
        estimated_hours: "",
      });
      if (projectId) loadMilestones(projectId);
    }
    setShowModal(true);
  }

  function openDetails(task) {
    setDetailTask(task);
    setShowDetailsModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      title: form.title || null,
      description: form.description || null,
      project_id: form.project_id ? Number(form.project_id) : null,
      milestone_id: form.milestone_id ? Number(form.milestone_id) : null,
      due_date: form.due_date || null,
      status: form.status || "todo",
      priority: form.priority || "medium",
      estimated_hours:
        form.estimated_hours !== "" && form.estimated_hours !== null
          ? Number(form.estimated_hours)
          : null,
    };

    try {
      if (selectedTask) {
        await API.put(`/tasks/${selectedTask.id}`, payload);
      } else {
        await API.post("/tasks", payload);
      }
      setShowModal(false);
      const reloadPid =
        payload.project_id ||
        (projectId
          ? Number(projectId)
          : form.project_id
          ? Number(form.project_id)
          : null);
      if (reloadPid) loadTasks(reloadPid);
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || "Failed to save task";
      alert(msg);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this task?")) return;
    try {
      await API.delete(`/tasks/${id}`);
      const pid = projectId
        ? Number(projectId)
        : form.project_id
        ? Number(form.project_id)
        : null;
      if (pid) loadTasks(pid);
      setShowDetailsModal(false);
    } catch (err) {
      alert(err.response?.data?.error || "Delete failed");
    }
  }

  function statusLabel(token) {
    switch (token) {
      case "todo":
        return "To Do";
      case "in_progress":
        return "In Progress";
      case "done":
        return "Done";
      default:
        return token;
    }
  }

  function truncateWords(text, n = 3) {
    if (!text && text !== 0) return "-";
    const words = String(text).trim().split(/\s+/);
    if (words.length <= n) return words.join(" ");
    return words.slice(0, n).join(" ") + "…";
  }

  return (
    <div className="tasks-page">
      <div className="header-row">
        <h2>Tasks Management</h2>

        <select
          className="dropdown"
          value={form.project_id}
          onChange={(e) => {
            const pid = e.target.value;
            setForm((f) => ({ ...f, project_id: pid }));
            loadMilestones(pid);
            loadTasks(pid);
          }}
        >
          <option value="">Select Project</option>
          {projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </select>

        <button className="add-btn" onClick={() => openModal()}>
          <Plus size={18} /> Add Task
        </button>
      </div>

      <table className="tasks-table">
        <thead>
          <tr>
            <th>Title</th>
            <th className="desc-col">Description</th>
            <th>Milestone</th>
            <th>Due</th>
            <th>Status</th>
            <th>Priority</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <tr
              key={t.id}
              onClick={(e) => {
                openDetails(t);
              }}
              style={{ cursor: "pointer" }}
            >
              <td>{t.title}</td>
              <td className="desc-cell" title={t.description || ""}>
                <span className="desc-short">
                  {truncateWords(t.description, 3)}
                </span>
              </td>
              <td>{t.milestone_title || "-"}</td>
              <td>
                {t.due_date ? new Date(t.due_date).toLocaleDateString() : "-"}
              </td>
              <td>{statusLabel(t.status)}</td>
              <td>{t.priority}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedTask ? "Edit Task" : "Add New Task"}</h3>
            <form onSubmit={handleSubmit} className="task-form">
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={form.title}
                onChange={handleChange}
                required
              />
              <select
                name="project_id"
                value={form.project_id}
                onChange={(e) => {
                  const pid = e.target.value;
                  setForm((f) => ({ ...f, project_id: pid }));
                  loadMilestones(pid);
                }}
                required
                className="dropdown"
              >
                <option value="">Select Project</option>
                {projects.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </select>
              <select
                name="milestone_id"
                value={form.milestone_id}
                onChange={handleChange}
                className="dropdown"
              >
                <option value="">Select Milestone</option>
                {milestones.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    {m.title}
                  </option>
                ))}
              </select>
              <input
                type="date"
                name="due_date"
                value={form.due_date}
                onChange={handleChange}
              />
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="dropdown"
              >
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
              <select
                name="priority"
                value={form.priority}
                onChange={handleChange}
                className="dropdown"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <input
                type="number"
                name="estimated_hours"
                placeholder="Est. Hours"
                value={form.estimated_hours}
                onChange={handleChange}
                min="0"
                step="0.25"
              />
              <textarea
                name="description"
                placeholder="Task description"
                value={form.description}
                onChange={handleChange}
              />
              <div className="modal-actions">
                <button type="submit">
                  {selectedTask ? "Update Task" : "Add Task"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && detailTask && (
        <div
          className="modal-overlay"
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            className="modal details-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Task Details</h3>
            <div className="detail-row">
              <strong>Title:</strong> {detailTask.title}
            </div>
            <div className="detail-row">
              <strong>Project:</strong>{" "}
              {detailTask.project_name || detailTask.project_id || "—"}
            </div>
            <div className="detail-row">
              <strong>Milestone:</strong> {detailTask.milestone_title || "—"}
            </div>
            <div className="detail-row">
              <strong>Due:</strong>{" "}
              {detailTask.due_date
                ? new Date(detailTask.due_date).toLocaleString()
                : "—"}
            </div>
            <div className="detail-row">
              <strong>Status:</strong> {statusLabel(detailTask.status)}
            </div>
            <div className="detail-row">
              <strong>Priority:</strong> {detailTask.priority}
            </div>
            <div className="detail-row" style={{ marginTop: 10 }}>
              <strong>Description:</strong>
              <div className="detail-desc">
                {detailTask.description || "—"}
              </div>
            </div>

            {detailTask.assignments && detailTask.assignments.length > 0 && (
              <div className="detail-row" style={{ marginTop: 10 }}>
                <strong>Assignments:</strong>
                <ul className="assign-list">
                  {detailTask.assignments.map((a, idx) => (
                    <li key={idx}>
                      {a.employee_name || a.employee_id} —{" "}
                      {a.role_in_task || "member"} ({a.status || "—"})
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: 12 }}>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn-close"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  openModal(detailTask);
                }}
                style={{ marginLeft: 8 }}
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(detailTask.id)}
                style={{ marginLeft: 8, background: "#ef4444", color: "#fff" }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
